import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/api/client'
import { useAuth } from '@/hooks/use-auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Plus } from 'lucide-react'
import { HelpBanner } from '@/components/ui/help-banner'
import type { PaginatedResponse, BookmakerResponse, TenantResponse } from '@/api/types'

export function BookmakersPage() {
  const { t } = useTranslation(['tenants', 'common'])
  const { user } = useAuth()
  const [offset, setOffset] = useState(0)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDomain, setNewDomain] = useState('')
  const [newBookmakerId, setNewBookmakerId] = useState('')
  const [newTenantId, setNewTenantId] = useState('')
  const queryClient = useQueryClient()
  const limit = 20

  const { data, isLoading } = useQuery({
    queryKey: ['bookmakers', offset],
    queryFn: () => api.get<PaginatedResponse<BookmakerResponse>>(`/bookmakers?offset=${offset}&limit=${limit}`),
  })

  const { data: tenantsData } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => api.get<PaginatedResponse<TenantResponse>>('/tenants?limit=100'),
  })

  const tenantMap = new Map(tenantsData?.data.data?.map(tn => [tn.id, tn.name]) ?? [])

  const createMutation = useMutation({
    mutationFn: (params: { name: string; domain: string; bookmaker_id?: number }) => {
      const tenantId = newTenantId || user?.tenants[0]?.id
      return api.post(`/tenants/${tenantId}/bookmakers`, params)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookmakers'] })
      setDialogOpen(false)
      setNewName('')
      setNewDomain('')
      setNewBookmakerId('')
      setNewTenantId('')
    },
  })

  const bookmakers = data?.data.data ?? []
  const total = data?.data.total ?? 0

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold">{t('nav.bookmakers', { ns: 'common' })}</h2>
          <p className="text-sm text-muted-foreground">{t('bookmakers.subtitle')}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger render={<Button />}>
            <Plus className="h-4 w-4 mr-2" />{t('bookmakers.newBookmaker')}
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t('bookmakers.createBookmaker')}</DialogTitle></DialogHeader>
            <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate({ name: newName, domain: newDomain, bookmaker_id: newBookmakerId ? parseInt(newBookmakerId) : undefined }) }} className="flex flex-col gap-4">
              <div>
                <Label htmlFor="bookmaker-tenant">{t('labels.tenant', { ns: 'common' })}</Label>
                <select
                  id="bookmaker-tenant"
                  value={newTenantId}
                  onChange={(e) => setNewTenantId(e.target.value)}
                  className="w-full text-sm border border-input rounded-md px-3 py-2 bg-background"
                  required
                >
                  <option value="">{t('placeholders.selectTenant', { ns: 'common' })}</option>
                  {(tenantsData?.data.data ?? []).map(tn => (
                    <option key={tn.id} value={tn.id}>{tn.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="bookmaker-name">{t('labels.name', { ns: 'common' })}</Label>
                <Input id="bookmaker-name" value={newName} onChange={(e) => setNewName(e.target.value)} required />
              </div>
              <div>
                <Label htmlFor="bookmaker-domain">{t('labels.domain', { ns: 'common' })}</Label>
                <Input id="bookmaker-domain" value={newDomain} onChange={(e) => setNewDomain(e.target.value)} placeholder={t('placeholders.domainExample', { ns: 'common' })} />
              </div>
              <div>
                <Label htmlFor="bookmaker-bookmaker-id">{t('bookmakers.bookmakerId')}</Label>
                <Input id="bookmaker-bookmaker-id" type="number" value={newBookmakerId} onChange={(e) => setNewBookmakerId(e.target.value)} placeholder={t('bookmakers.bookmakerIdPlaceholder')} />
              </div>
              <Button type="submit" disabled={createMutation.isPending}>{t('buttons.create', { ns: 'common' })}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <HelpBanner storageKey="bookmakers">
        <p>{t('bookmakers.help.line1')}</p>
        <p>{t('bookmakers.help.line2')}</p>
      </HelpBanner>

      {isLoading ? <p className="text-muted-foreground">{t('loading', { ns: 'common' })}</p> : (
        <>
          <div className="border rounded-md">
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-muted/50">
                <th className="text-left p-3 font-medium">{t('labels.name', { ns: 'common' })}</th>
                <th className="text-left p-3 font-medium">{t('labels.domain', { ns: 'common' })}</th>
                <th className="text-left p-3 font-medium">{t('bookmakers.bookmakerId')}</th>
                <th className="text-left p-3 font-medium">{t('labels.tenant', { ns: 'common' })}</th>
                <th className="text-left p-3 font-medium">{t('labels.status', { ns: 'common' })}</th>
                <th className="text-left p-3 font-medium">{t('labels.created', { ns: 'common' })}</th>
              </tr></thead>
              <tbody>
                {bookmakers.map((s) => (
                  <tr key={s.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="p-3">{s.name}</td>
                    <td className="p-3 text-muted-foreground">{s.domain || '—'}</td>
                    <td className="p-3 text-muted-foreground">{s.bookmaker_id ?? '—'}</td>
                    <td className="p-3 text-muted-foreground">{tenantMap.get(s.tenant_id) ?? s.tenant_id}</td>
                    <td className="p-3"><Badge variant={s.status === 'active' ? 'default' : 'secondary'}>{s.status}</Badge></td>
                    <td className="p-3 text-muted-foreground">{new Date(s.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
                {bookmakers.length === 0 && <tr><td colSpan={6} className="p-3 text-center text-muted-foreground">{t('bookmakers.empty')}</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm text-muted-foreground">{t('labels.total', { ns: 'common' })}: {total}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>{t('pagination.previous', { ns: 'common' })}</Button>
              <Button variant="outline" size="sm" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}>{t('pagination.next', { ns: 'common' })}</Button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
